<template>
    <div class="">
        <div class="container">
            <div class="spacer s20"></div>

            <h1 v-if="!hasData">OffshoreSMS Vessel Finder</h1>
            <h1 v-else>
                {{ vesselData.vesselName }} <span><font-awesome-icon icon="check-circle" /></span>
            </h1>

            <h2 v-if="attempts < 6">Find any marine asset on OffshoreSMS to view its public profile.</h2>

            <div v-if="!hasData" class="spacer s20"></div>

            <div v-if="attempts < 6" class="tabs is-centered is-boxed">
                <ul>
                    <li @click="tab = 'code'" :class="[tab === 'code' ? 'is-active' : '']">
                        <a @click="tab = 'code'"><font-awesome-icon icon="key" /> &nbsp; <span> Search by Code</span></a>
                    </li>

                    <li @click="tab = 'name'" :class="[tab === 'name' ? 'is-active' : '']">
                        <a @click="tab = 'name'"><font-awesome-icon icon="star" /> &nbsp; <span> Search by Name</span></a>
                    </li>
                </ul>
            </div>

            <div class="tab-content" v-if="attempts < 6">
                <!-- Search by asset code ========================================  -->
                <div class="tab-pane" v-if="tab === 'code'">
                    <h3 v-if="!checkingCode">Enter a 6 digit vessel code below.</h3>

                    <div v-if="!hasData" class="spacer s20"></div>

                    <div class="code-entry">
                        <div v-if="!checkingCode" class="input-wrapper" :class="{ 'has-value': code.length > 0 }">
                            <input
                                ref="code"
                                type="tel"
                                maxlength="6"
                                placeholder="Enter code"
                                @keyup.enter="checkCode()"
                                @keypress="isNumber($event)"
                                v-model="code" />
                        </div>

                        <div v-if="!checkingCode && !hasData" class="buttons centered">
                            <button
                                :disabled="checkingCode || code.length != 6"
                                @click="checkCode()"
                                class="button is-medium"
                                :class="[attempts > 4 ? 'is-warning' : 'is-primary']">
                                <font-awesome-icon icon="search" /> &nbsp;Search <span v-if="attempts > 0"> &nbsp;({{ attempts }})</span>
                            </button>
                            <button v-if="code.length > 0" @click="clearCode()" class="button is-medium is-light">
                                <font-awesome-icon icon="times" /> &nbsp; Clear
                            </button>
                        </div>

                        <div v-if="loading" class="loading-code">
                            <span><font-awesome-icon icon="sync-alt" class="fa-spin" /> &nbsp; {{ loadingText }}</span>
                        </div>
                    </div>

                    <div v-if="!hasData" class="results">
                        <div class="spacer s20"></div>
                        <h3>{{ result.heading }}</h3>
                        <p>{{ result.text }}</p>
                        <div class="spacer s20"></div>
                        <button v-if="showClear" @click="clearCode()" class="button is-medium is-warning">Search again</button>
                    </div>

                    <div v-if="hasData" class="vessel-data">
                        <div class=""><span>Vessel Owner:</span> {{ vesselData.vesselOwnerName }}</div>
                        <div class="">
                            <span>Home Port:</span> {{ vesselData.homePort || vesselData.homePort == '' ? vesselData.homePort : 'Unknown' }}
                        </div>
                        <div class="spacer s20"></div>
                        <button v-if="showClear" @click="clearCode()" class="button is-medium is-warning">Search again</button>
                    </div>
                </div>

                <!-- Search by asset name ========================================  -->
                <div class="tab-pane" v-if="tab === 'name'">
                    <h3 v-if="!searchStarted">Search by vessel name.</h3>
                    <div v-if="!searchStarted" class="italic blue">(Tip: start with the first part of the vessel name)</div>

                    <div class="spacer s20"></div>

                    <div class="code-entry">
                        <div v-if="!searchStarted" class="input-wrapper text" :class="{ 'has-value': findName.length > 0 }">
                            <input
                                ref="assetname"
                                type="text"
                                maxlength="30"
                                placeholder="Type vessel name..."
                                @keyup.enter="findByName()"
                                v-model="findName" />
                        </div>

                        <div class="buttons centered">
                            <button
                                v-if="!searchStarted"
                                :disabled="findName.length < 4"
                                @click="findByName()"
                                class="button is-medium"
                                :class="[attempts > 4 ? 'is-warning' : 'is-primary']">
                                <font-awesome-icon icon="search" /> &nbsp;Search <span v-if="attempts > 0"> &nbsp;({{ attempts }})</span>
                            </button>
                            <button v-if="!searchStarted && findName.length > 0" @click="clearNameSearch()" class="button is-medium is-light">
                                <font-awesome-icon icon="times" /> &nbsp; Clear
                            </button>
                        </div>

                        <div v-if="searching" class="loading-code">
                            <span><font-awesome-icon icon="sync-alt" class="fa-spin" /> &nbsp; Searching asset name...</span>
                        </div>
                    </div>

                    <div v-if="attempts < 6">
                        <div v-if="filteredResults.length > 0" class="search-results">
                            <h3 class="bold blue">Click a search result below to view the marine asset:</h3>
                            <div class="spacer s20"></div>
                            <div class="results-wrapper">
                                <div v-for="(asset, idx) in filteredResults" :key="`result-${idx}`">
                                    <div class="search-result" @click="showCrewInfo(asset)">
                                        <div class="profile-avatar">
                                            <div
                                                v-if="asset.avatarUrl && asset.avatarUrl != ''"
                                                class="avatar"
                                                :style="{ backgroundImage: `url(${asset.avatarUrl})` }"></div>
                                            <div v-else class="round-button-letters">
                                                {{ getInitials(asset.vesselName) }}
                                            </div>
                                        </div>
                                        <div class="asset-details">
                                            <div class="asset-name">{{ truncate(asset.vesselName, 20) }}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="spacer s20"></div>
                            <button @click="clearNameSearch()" class="button is-medium is-warning">Search again</button>
                        </div>
                        <div v-else>
                            <p v-if="!searching && findName != '' && searchStarted">No matches found. Please try another name search.</p>
                            <div class="spacer s20"></div>
                            <button
                                v-if="!searching && findName != '' && searchStarted"
                                @click="clearNameSearch()"
                                class="button is-medium is-warning">
                                Search again
                            </button>
                        </div>
                    </div>
                    <div v-else class="red mt-4">Too many searches. Please try again later.</div>
                </div>
            </div>
            <div v-else class="red mt-4">
                <div v-if="!hasData" class="spacer s20"></div>
                <p><font-awesome-icon icon="exclamation-triangle" /> &nbsp; Too many searches. Please try again later.</p>
            </div>

            <!-- <hr />

            <h5 v-if="attempts > 0" class="red">Attempts: {{ attempts }}</h5> -->

            <div class="spacer s100"></div>

            <!-- <div class="extras">

    <a href="http://offshoresms.com.au" class="button is-medium is-primary">Visit OffshoreSMS</a>


    <div class="download centered">
      <a href="https://apps.apple.com/us/app/offshoresms/id1526656158" target="_blank"><img class="partners" src="@/assets/app-store.png" /></a>
      <a href="https://play.google.com/store/apps/details?id=offshoresms.appdev.nybble.com.smsapp" target="_blank"><img class="partners" src="@/assets/play-store.png" /></a>
    </div>

</div> -->
        </div>

        <!-- objectID, vesselName, vesselOwnerName, vesselOwnerID, isCompanyVessel, vesselState, avatarUrl, -->

        <simple-modal :title="selectedAsset.vesselName" theme="is-info" @closed="close()" v-if="showAssetModal">
            <div
                v-if="selectedAsset.avatarUrl && selectedAsset.avatarUrl != ''"
                class="big-avatar"
                :style="{ backgroundImage: `url(${selectedAsset.avatarUrl})` }"></div>
            <table class="table is-fullwidth is-striped">
                <tbody>
                    <tr>
                        <th>Asset Name</th>
                        <td>{{ selectedAsset.vesselName }}</td>
                    </tr>
                    <tr>
                        <th>Asset Location</th>
                        <td>{{ selectedAsset.vesselState && selectedAsset.vesselState != '' ? selectedAsset.vesselState : 'Not set' }}</td>
                    </tr>
                    <tr>
                        <th>Asset Owner</th>
                        <td>
                            {{ selectedAsset.vesselOwnerName && selectedAsset.vesselOwnerName != '' ? selectedAsset.vesselOwnerName : 'Not set' }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="buttons centered">
                <span class="button is-large is-primary" @click="toAsset()"><font-awesome-icon icon="check" /> &nbsp; Select this marine asset</span>
            </div>
            <div class="spacer s10"></div>
        </simple-modal>
    </div>
</template>

<script>
import { db } from '../main.js';
import { collection, doc, onSnapshot, setDoc, getDoc } from 'firebase/firestore';
import NProgress from 'nprogress';
import { getFunctions, httpsCallable } from 'firebase/functions';

const functions = getFunctions();
const addRevision = httpsCallable(functions, 'addRevision');
const addVesselRevision = httpsCallable(functions, 'addVesselRevision');
const adminRetrieveUserData = httpsCallable(functions, 'adminRetrieveUserData');
const searchAlgoliaVessels = httpsCallable(functions, 'searchAlgoliaVessels');
const SimpleModal = () => import('../reusables/SimpleModal.vue');

export default {
    components: {
        SimpleModal
    },
    mounted: function () {
        setTimeout(() => {
            this.$refs.code.focus();
            // this.$refs.assetname.focus();
        }, 100);
        this.$store.dispatch('CLEARSELECTEDVESSEL');
        // if (this.sentCode && this.sentCode != '') {
        //     this.code = this.sentCode;
        //     this.checkCode();
        // }
        let hasParam = this.$route.query && this.$route.query.c;
        if (hasParam) {
            let c = this.$route.query.c.trim();
            if (c != '') {
                this.code = c;
                this.checkCode();
            }
        }
    },

    props: {
        sentCode: {
            type: String,
            default: ''
        }
    },

    computed: {
        currentVessel() {
            return this.$store.getters.getCurrentVessel;
        },
        filteredResults() {
            return this.searchResults.filter((m) => m.isPrivate == false && m.isExpired == false && m.vesselOwnerName != 'XYZ Orphan');
            // !m.isPrivate && !m.isExpired && !m.vesselOwnerName == 'XYZ Orphan'
        }
        // showFindButton() {
        //     return !this.checkingCode && ((this.findName.length == 0 && this.code.length > 5) || (this.code.length == 0 && this.findName.length > 3));
        //     // return this.code.length != 6 || this.checkingCode;
        // },
        // hideClear() {
        //     return (this.code.length == 0 && this.findName.length == 0) || this.checkingCode;
        // }
    },

    data: function () {
        return {
            tab: 'code',
            code: '',
            findName: '',
            checkingCode: false,
            result: {
                heading: '',
                text: ''
            },
            showClear: false,
            loading: false,
            loadingText: 'Checking code...',
            vesselData: {},
            hasData: false,

            searching: false,
            searchStarted: false,
            searchResults: [],
            secureSearchTerm: '',
            attempts: 0,
            showAssetModal: false
        };
    },

    methods: {
        close() {
            this.showAssetModal = false;
        },

        isNumber: function ($evt) {
            $evt = $evt ? $evt : window.event;
            var charCode = $evt.which ? $evt.which : $evt.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46) {
                $evt.preventDefault();
            } else {
                return true;
            }
        },

        toPage(page) {
            this.$router.push({
                name: page,
                params: {}
            });
            NProgress.done();
        },

        goBack() {
            this.$router.go(-1);
        },

        clearNameSearch() {
            this.findName = '';
            this.searchResults = [];
            this.searching = false;
            this.searchStarted = false;
        },

        async findByName() {
            if (this.findName.length < 4) return;
            // console.log('searching for', this.findName);
            this.searching = true;
            this.searchStarted = true;
            this.searchResults = [];
            if (this.findName == '') {
                this.searchResults = [];
                this.searching = false;
                // this.searchStarted = false;
            } else {
                await searchAlgoliaVessels({
                    searchTerm: this.findName,
                    limit: 20
                }).then((result) => {
                    // console.log('result', result.data.text);
                    this.searchResults = result.data.text;
                    this.searching = false;
                    // this.searchStarted = false;
                });
            }
            const sleep = (milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds));
            await sleep(2000);
            NProgress.done();
            this.attempts++;
        },

        async checkCode() {
            if (this.code == '' || this.code.length < 6) return;
            NProgress.start();
            // console.log('checking code', this.code);
            window.scrollTo(0, 0);
            // console.log(this.code);
            let self = this;
            this.checkingCode = true;
            this.loading = true;
            this.attempts++;

            if (this.code == '123456') {
                this.toPage('Home');
                return;
            }

            const docRef = doc(db, 'vesselManualCodes', this.code);
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                self.loadingText = 'Fetching vessel information...';
                // console.log('FOUND:', docSnap.data().vesselID, docSnap.data().vesselName);
                // self.loadVesselData(docSnap.data().vesselID);

                addRevision({
                    userID: '',
                    userName: '',
                    description:
                        'Someone has successfully searched for a vessel with code (' +
                        self.code +
                        ') and vesselID (' +
                        docSnap.data().vesselID +
                        ') and vesselName (' +
                        docSnap.data().vesselName +
                        ') on OffshoreSMS Go.'
                });
                // addVesselRevision({
                //     userName: '',
                //     userID: '',
                //     vesselName: docSnap.data().vesselName ?? 'No vessel name',
                //     vesselID: docSnap.data().vesselID,
                //     revisionDetails: 'Someone has successfully searched for this vessel on go.offshoresms.com.au with code (' + self.code + ')'
                // });

                this.$router.push({
                    name: 'VesselInfo',
                    // params: {
                    //     sentVessel: sentVessel
                    // },
                    query: {
                        vesselid: docSnap.data().vesselID,
                        screenid: 'vessel'
                    }
                });
            } else {
                self.result.heading = 'No vessel found, sorry.';
                self.result.text = 'Please check the code or search again.';
                self.loading = false;
                self.showClear = true;
                self.loadingText = 'Checking code...';
                NProgress.done();
            }
        },

        truncate(string, limit) {
            // console.log(string,limit);
            if (string == undefined || string == '') return '';
            return string.length > limit ? string.slice(0, limit) + '...' : string;
        },

        toAsset() {
            // console.log(this.selectedAsset);
            this.$router.push({
                name: 'VesselInfo',
                // params: {
                //     sentVessel: sentVessel
                // },
                query: {
                    vesselid: this.selectedAsset.vesselID,
                    screenid: 'vessel'
                }
            });
        },

        showCrewInfo(asset) {
            this.selectedAsset = asset;
            this.showAssetModal = true;
        },

        getInitials(title) {
            if (title == undefined || title == '') return 'NN';
            let parts = title.split(' ');
            let initials = '';
            if (parts.length == 1) return parts[0][0];
            for (let i = 0; i < 2; i++) {
                if (parts[i] != undefined && parts[i].length > 0 && parts[i] !== '') {
                    initials += parts[i][0];
                }
            }
            return initials;
        },

        // async loadVesselData(vesselID) {
        //     // console.log('VID',vesselID);
        //     let self = this;
        //     const docRef = doc(db, 'vessels', vesselID);
        //     const docSnap = await getDoc(docRef);
        //     if (docSnap.exists()) {
        //         // console.log('exists@!');
        //         // console.log(docSnap.data().vesselName);
        //         self.vesselData = docSnap.data();
        //         self.result.heading = docSnap.data().vesselName ? docSnap.data().vesselName : 'No vessel name';
        //         self.result.text = '';
        //         self.loading = false;
        //         self.showClear = true;
        //         self.loadingText = 'Checking code...';
        //         self.hasData = true;
        //         NProgress.done();
        //         let query = {
        //             vesselid: vesselID,
        //             screenid: 'vessel'
        //         };
        //         let sentVessel = {
        //             vesselID: vesselID
        //         };
        //         // console.log('query',query);

        //         // let allData = docSnap.data();
        //         // // vesselData["vesselID"] = vesselID;
        //         // // vesselData["savedAppInfoSections"] = docSnap.data().savedAppInfoSections ? docSnap.data().savedAppInfoSections : [];
        //         // allData.vesselID = vesselID;
        //         // allData.savedAppInfoSections = docSnap.data().savedAppInfoSections ? docSnap.data().savedAppInfoSections : [];
        //         // self.$store.dispatch("UPDATESELECTEDVESSEL", { allData });

        //         self.$router.push({
        //             name: 'VesselInfo',
        //             // params: {
        //             //     sentVessel: sentVessel
        //             // },
        //             query: {
        //                 vesselid: vesselID,
        //                 screenid: 'vessel'
        //             }
        //         });
        //         // console.log('made this?');
        //         addRevision({
        //             userID: '',
        //             userName: '',
        //             description:
        //                 'Someone has successfully searched for a vessel with code (' +
        //                 self.code +
        //                 ') and vesselID (' +
        //                 vesselID +
        //                 ') on OffshoreSMS Go.'
        //         });
        //     } else {
        //         self.result.heading = 'No vessel found, sorry.';
        //         self.result.text = 'Please check the code or search again.';
        //         self.showClear = true;
        //         self.loading = false;
        //         self.loadingText = 'Checking code...';
        //         NProgress.done();
        //         addRevision({
        //             userID: '',
        //             userName: '',
        //             description: 'Failed vessel search with code (' + self.code + ') and vesselID (' + vesselID + ') on OffshoreSMS Go.'
        //         });
        //     }
        // },

        clearCode() {
            this.checkingCode = false;
            this.code = '';
            this.result = {
                heading: '',
                text: ''
            };
            this.hasData = false;
            this.showClear = false;
            setTimeout(() => {
                this.$refs.code.focus();
            }, 100);
        }
    }
};
</script>

<style lang="scss" scoped>
#app .container {
    max-width: 700px;
}
::-webkit-input-placeholder {
    /* Chrome/Opera/Safari */
    color: pink;
    letter-spacing: 1px;
}

::-moz-placeholder {
    /* Firefox 19+ */
    color: pink;
    letter-spacing: 1px;
}

:-ms-input-placeholder {
    /* IE 10+ */
    color: pink;
    letter-spacing: 1px;
}

:-moz-placeholder {
    /* Firefox 18- */
    color: pink;
    letter-spacing: 1px;
}

ul {
    list-style: none;
}

h1 {
    color: cadetblue;
}
h2 {
    margin-bottom: 12px;
    font-weight: bold;
    font-size: 1.1rem;
    color: deeppink;
}
h3 {
}

.results-wrapper {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 1rem;
    .search-result {
        display: flex;
        border: 1px solid #ddd;
        border-radius: 5px;
        padding: 6px 10px;
        align-items: center;
        margin: 0 8px 10px 0;
        cursor: pointer;
        transition: 0.25s;
        .profile-avatar {
            .round-button-letters {
                background: steelblue;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 1rem;
                font-weight: bold;
                color: #fff;
                width: 42px;
                height: 42px;
                border-radius: 50%;
                margin-right: 6px;
                border: 2px solid #fff;
            }
            .avatar {
                width: 42px;
                height: 42px;
                display: block;
                background-size: cover;
                background-position: center;
                border-radius: 50%;
                border: 2px solid #fff;
                margin-right: 6px;
            }
        }
        .asset-details {
            font-weight: bold;
            color: steelblue;
            transition: 0.25s;
            margin-right: 5px;
        }
        &:hover {
            background: steelblue;
            .asset-details {
                color: #fff;
            }
        }
    }
}

.big-avatar {
    width: 150px;
    height: 150px;
    display: block;
    background-size: cover;
    background-position: center;
    border-radius: 50%;
    border: 2px solid #fff;
    margin: 0 auto 1rem;
}

.download {
    margin: 2rem auto;
    max-width: 200px;
}

.code-entry {
    ::placeholder {
        color: #fff;
    }
    .input-wrapper {
        max-width: 280px;
        margin: 0 auto 2rem;
        padding: 3px;
        border: 2px solid steelblue;
        border-radius: 5px;
        input {
            padding: 1rem;
            background: #99d5fa;
            // background: steelblue;
            border: none;
            font-size: 1.5rem;
            letter-spacing: 5px;
            text-transform: uppercase;
            text-align: center;
            max-width: 280px;
            color: #fff;
            // outline: -webkit-focus-ring-color auto thin;
            // outline: revert;
            // outline: #f00 1px solid;
            display: block;
            width: 100%;
            margin: 0;
            border-radius: 2px;
            transition: 0.3s;
            &:focus {
                outline: none;
                // background-color: steelblue;
            }
        }
        &.has-value {
            input {
                background-color: steelblue;
            }
        }
        &.text {
            max-width: 400px;

            // ::placeholder {
            //     color: #fff;
            // }
            input {
                // background-color: lightskyblue;
                text-transform: none;
                // color: #fff;
                max-width: 400px;
            }
        }
    }
}

.extras {
    padding-top: 1rem;
    border-top: 1px solid #e8e8e8;
    width: 80%;
    margin: 5rem auto 3rem;
}

.results {
    .heading {
    }

    .text {
    }
}
</style>
